<script setup lang="ts">
/**
 * One row of a plain list in a side panel or card: a leading mark (colour
 * dot, icon, avatar), a title, a subtitle and a meta line, an optional
 * trailing value, row actions and a footer.
 *
 * The body is ONE click target — a link with `href`, a button that emits
 * `open` while `clickable` (default), or plain text with `clickable: false`.
 * `#actions` sit beside the click target and `#footer` below it, so their
 * buttons stay separate controls and never nest inside the row button.
 *
 * `icon` shows a type icon in a tinted tile as the leading mark (a device, an
 * app) — `iconTone` picks the tint (brand by default); the same tile sizes as
 * NxPageHeader's icon tile, smaller when `dense`.
 *
 * `option` makes the row a listbox option whose owner keeps the focus (an
 * editor's suggestion list): no button, no tab stop, `mousedown` cancelled.
 *
 * Differs from `NxFeedItem` (unread dot, relative time, hover actions) and
 * `NxItemCard` (accent strip, badges, due date): this is the neutral row.
 */
import { computed, inject, type Component } from 'vue';
import { accentVars, type NxAccentTone } from '../Panel/accent';
import { listRowTagKey } from './internal';

const props = withDefaults(
  defineProps<{
    title?: string;
    /** Second line, e.g. „Mo., 2. Mai · Arbeit“. */
    subtitle?: string;
    /** Third, smaller line, e.g. „gelöscht 02.05.26, 09:14“. */
    meta?: string;
    /** `false` renders the body as text without a click target. */
    clickable?: boolean;
    /** Renders the body as a link. */
    href?: string;
    selected?: boolean;
    /**
     * The `aria-current` token a selected row states: `page` for the current
     * page/sheet in a navigation list, `step` in a process, `location`,
     * `date`, `time`, or the generic `true` (default). Only set while
     * `selected` and the row is a click target.
     */
    ariaCurrent?: 'page' | 'step' | 'location' | 'date' | 'time' | 'true';
    disabled?: boolean;
    /** Tighter padding for dense lists. */
    dense?: boolean;
    /** Wrapper element; defaults to `li` inside `NxList`, else `div`. */
    tag?: string;
    /**
     * The row opens and closes its `#footer` (a disclosure row): the button
     * states it (`aria-expanded`). Unset for a row that opens nothing.
     */
    expanded?: boolean;
    /**
     * The row is an on/off toggle (a calendar shown or hidden): the button
     * states it (`aria-pressed`). Unset for a row that toggles nothing.
     */
    pressed?: boolean;
    /**
     * Type icon in a tinted tile as the leading mark (after `#leading`, if
     * both are given). Decorative: the title names the row.
     */
    icon?: Component;
    /** Tint of the icon tile. Defaults to `brand`. */
    iconTone?: NxAccentTone;
    /**
     * Title, subtitle and meta wrap instead of ending in an ellipsis — a row
     * whose second line is a sentence to read (a status with its hint).
     */
    wrap?: boolean;
    /**
     * How `#footer` lays out its content. `row` (default): a wrapping row of
     * small items — a reply button, a hint. `nested`: the footer holds the
     * row's children — a nested `NxList` and what belongs to it („alle n
     * zeigen“, a note): a column indented under the row with a hairline on the
     * start side, the nested list (or the first child) at full width — a row
     * with a group below it, e.g. a chain group's documents.
     */
    footerLayout?: 'row' | 'nested';
    /**
     * Attributes bound onto the footer element itself — the `#footer` slot
     * sits inside it, so this is the way to give the footer an `id` (the
     * target of a chevron's `aria-controls`) or a test hook.
     */
    footerAttrs?: Record<string, unknown>;
    /**
     * The row is an option of a listbox whose owner keeps the focus — an
     * editor's suggestion list that names the active row through
     * `aria-activedescendant`. The row is one `role="option"` element with no
     * tab stop and nothing focusable inside; `mousedown` is cancelled, so a
     * pick never takes the focus from the owner. `selected` marks the active
     * option (`aria-selected`). Give it an `id` and put the rows in an element
     * with `role="listbox"`, not in an `NxList`. Default false.
     */
    option?: boolean;
  }>(),
  {
    title: undefined,
    subtitle: undefined,
    meta: undefined,
    clickable: true,
    href: undefined,
    selected: false,
    ariaCurrent: 'true',
    disabled: false,
    dense: false,
    tag: undefined,
    expanded: undefined,
    pressed: undefined,
    icon: undefined,
    iconTone: 'brand',
    wrap: false,
    footerLayout: 'row',
    footerAttrs: undefined,
    option: false,
  }
);

const emit = defineEmits<{
  /** The body was activated (also fires for a link, before the browser follows it). */
  open: [event: MouseEvent];
}>();

defineSlots<{
  /** Leading mark: colour dot, icon or avatar. */
  leading?(): unknown;
  /** Replaces the title text. */
  title?(): unknown;
  /** Replaces the subtitle text. */
  subtitle?(): unknown;
  /** Replaces the meta text. */
  meta?(): unknown;
  /** Right-aligned value inside the click target (time, count). */
  trailing?(): unknown;
  /** Row actions beside the click target. */
  actions?(): unknown;
  /** Full-width content below the row (response buttons, hints). */
  footer?(): unknown;
}>();

const tileStyle = computed(() => accentVars(props.iconTone, undefined, '--row-tile'));

const injectedTag = inject(listRowTagKey, 'div');
const wrapperTag = computed(() => props.tag ?? injectedTag);

const mainTag = computed(() => {
  if (props.option) return 'div';
  if (props.href && !props.disabled) return 'a';
  return props.clickable ? 'button' : 'div';
});
const isInteractive = computed(() => props.option || mainTag.value !== 'div');

const optionAttrs = computed(() =>
  props.option
    ? {
        role: 'option',
        'aria-selected': props.selected,
        'aria-disabled': props.disabled || undefined,
        onMousedown: (event: MouseEvent) => event.preventDefault(),
      }
    : {}
);

function onClick(event: MouseEvent): void {
  if (!isInteractive.value || props.disabled) return;
  emit('open', event);
}
</script>

<template>
  <component
    :is="wrapperTag"
    class="nx-list-row"
    :class="{
      'is-interactive': isInteractive,
      'is-selected': selected,
      'is-disabled': disabled,
      'is-dense': dense,
      'is-wrapping': wrap,
    }"
    data-testid="nx-list-row"
    v-bind="optionAttrs"
  >
    <div class="nx-list-row__line">
      <component
        :is="mainTag"
        class="nx-list-row__main"
        :href="mainTag === 'a' ? href : undefined"
        :type="mainTag === 'button' ? 'button' : undefined"
        :disabled="mainTag === 'button' ? disabled : undefined"
        :aria-current="selected && mainTag !== 'div' ? ariaCurrent : undefined"
        :aria-expanded="mainTag === 'button' && expanded !== undefined ? expanded : undefined"
        :aria-pressed="mainTag === 'button' && pressed !== undefined ? pressed : undefined"
        @click="onClick"
      >
        <span
          v-if="$slots.leading || icon"
          class="nx-list-row__leading"
          :class="{ 'has-tile': !!icon }"
        >
          <slot name="leading" />
          <span v-if="icon" class="nx-list-row__tile" :style="tileStyle" aria-hidden="true">
            <component :is="icon" :class="dense ? 'nxt-icon-md' : 'nxt-icon-lg'" />
          </span>
        </span>
        <span class="nx-list-row__body">
          <span v-if="$slots.title || title" class="nx-list-row__title">
            <slot name="title">{{ title }}</slot>
          </span>
          <span v-if="$slots.subtitle || subtitle" class="nx-list-row__subtitle">
            <slot name="subtitle">{{ subtitle }}</slot>
          </span>
          <span v-if="$slots.meta || meta" class="nx-list-row__meta">
            <slot name="meta">{{ meta }}</slot>
          </span>
        </span>
        <span v-if="$slots.trailing" class="nx-list-row__trailing">
          <slot name="trailing" />
        </span>
      </component>
      <div v-if="$slots.actions" class="nx-list-row__actions">
        <slot name="actions" />
      </div>
    </div>
    <div
      v-if="$slots.footer"
      v-bind="footerAttrs"
      class="nx-list-row__footer"
      :class="{ 'is-footer-nested': footerLayout === 'nested' }"
    >
      <slot name="footer" />
    </div>
  </component>
</template>

<style scoped>
.nx-list-row {
  display: flex;
  flex-direction: column;
  min-width: 0;
  border: var(--nxt-hairline) solid transparent;
  border-radius: var(--nxt-radius-md);
  list-style: none;
  font-size: inherit;
  transition:
    background-color var(--nxt-motion-fast) var(--nxt-ease-out),
    border-color var(--nxt-motion-fast) var(--nxt-ease-out);
}
.nx-list-row.is-interactive:not(.is-disabled):hover {
  background: var(--nxt-surface-sunken-bg);
  border-color: var(--nxt-border-subtle);
}
.nx-list-row.is-selected {
  background: var(--nxt-brand-subtle-bg);
  border-color: color-mix(in srgb, var(--nxt-brand-bg) 30%, transparent);
}
.nx-list-row.is-disabled {
  opacity: 0.6;
}

.nx-list-row__line {
  display: flex;
  align-items: center;
  min-width: 0;
}

.nx-list-row__main {
  flex: 1;
  min-width: 0;
  display: flex;
  align-items: center;
  gap: var(--nxt-space-xs);
  margin: 0;
  padding: var(--nxt-space-xs) var(--nxt-space-sm);
  border: 0;
  border-radius: inherit;
  background: transparent;
  color: inherit;
  font: inherit;
  text-align: start;
  text-decoration: none;
}
.nx-list-row.is-dense .nx-list-row__main {
  padding: var(--nxt-space-2xs) var(--nxt-space-xs);
}
.nx-list-row.is-interactive .nx-list-row__main {
  cursor: pointer;
}
.nx-list-row.is-disabled .nx-list-row__main {
  cursor: not-allowed;
}
.nx-list-row__main:focus-visible {
  outline: calc(var(--nxt-hairline) * 2) solid var(--nxt-focus-ring);
  outline-offset: calc(var(--nxt-hairline) * -2);
}

.nx-list-row__leading {
  flex: none;
  display: inline-grid;
  place-items: center;
  color: var(--nxt-fg-subtle);
}
/* A #leading mark and the tile side by side. */
.nx-list-row__leading.has-tile {
  grid-auto-flow: column;
  gap: var(--nxt-space-xs);
}
/* The icon tile: the tone's tint, the md tile of NxPageHeader's family. */
.nx-list-row__tile {
  display: grid;
  place-items: center;
  width: var(--nxt-tile-size-md);
  height: var(--nxt-tile-size-md);
  border-radius: var(--nxt-radius-lg);
  background: var(--row-tile-subtle-bg);
  color: var(--row-tile-fg);
}
.nx-list-row.is-dense .nx-list-row__tile {
  width: var(--nxt-tile-size-sm);
  height: var(--nxt-tile-size-sm);
  border-radius: var(--nxt-radius-md);
}

.nx-list-row__body {
  flex: 1;
  min-width: 0;
  display: flex;
  flex-direction: column;
  gap: calc(var(--nxt-space-2xs) / 2);
}
.nx-list-row__title,
.nx-list-row__subtitle,
.nx-list-row__meta {
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  /* Room for descenders: with the tight title line-height, `overflow: hidden`
     cut off the tail of a „g“ or „y“ (I-3178). The padding widens the painted
     box, the negative margin keeps the row's height. */
  padding-block-end: 0.15em;
  margin-block-end: -0.15em;
}
.nx-list-row.is-wrapping :is(.nx-list-row__title, .nx-list-row__subtitle, .nx-list-row__meta) {
  overflow: visible;
  white-space: normal;
  overflow-wrap: anywhere;
}
.nx-list-row__title {
  font-size: var(--nxt-font-size-sm);
  font-weight: var(--nxt-font-weight-medium);
  line-height: var(--nxt-line-height-tight);
  color: var(--nxt-fg);
}
.nx-list-row__subtitle {
  font-size: var(--nxt-font-size-xs);
  line-height: var(--nxt-line-height-normal);
  color: var(--nxt-fg-subtle);
}
.nx-list-row__meta {
  font-size: var(--nxt-font-size-xs);
  line-height: var(--nxt-line-height-normal);
  color: var(--nxt-fg-muted);
}

.nx-list-row__trailing {
  flex: none;
  font-size: var(--nxt-font-size-xs);
  color: var(--nxt-fg-muted);
}

.nx-list-row__actions {
  flex: none;
  display: flex;
  align-items: center;
  gap: var(--nxt-space-2xs);
  padding-inline-end: var(--nxt-space-xs);
}

.nx-list-row__footer {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: var(--nxt-space-2xs);
  padding: 0 var(--nxt-space-sm) var(--nxt-space-xs);
}
.nx-list-row.is-dense .nx-list-row__footer {
  padding: 0 var(--nxt-space-xs) var(--nxt-space-2xs);
}
/* footerLayout="nested": the children of the row sit under it, indented, a
   hairline on the start side; a nested list (the first child) takes the full
   width. The indent is the footer's own start padding plus a medium step, so
   the hairline sits where it would on a group inside a padded footer. After
   the dense rule, so only the start padding is replaced. */
.nx-list-row .nx-list-row__footer.is-footer-nested {
  flex-direction: column;
  flex-wrap: nowrap;
  align-items: flex-start;
  gap: var(--nxt-space-2xs);
  margin-inline-start: calc(var(--nxt-space-sm) + var(--nxt-space-md));
  padding-inline-start: var(--nxt-space-sm);
  border-inline-start: var(--nxt-hairline) solid var(--nxt-border-subtle);
}
.nx-list-row.is-dense .nx-list-row__footer.is-footer-nested {
  margin-inline-start: calc(var(--nxt-space-xs) + var(--nxt-space-md));
}
.nx-list-row__footer.is-footer-nested > :first-child {
  align-self: stretch;
}

@media (prefers-reduced-motion: reduce) {
  .nx-list-row {
    transition: none;
  }
}
</style>
